/* Public tour — the self-serve version of the vault peep. A visitor picks a chapter and
   watches it as long as they like, then leaves their number. Uses the same self-contained
   showcase components as the vault: no auth, no API, no real tenant data. */
import { useState } from "react";
import { Link } from "react-router-dom";
import ShowcaseWarRoom from "@/components/ShowcaseWarRoom";
import ShowcaseBatcher from "@/components/ShowcaseBatcher";
import ShowcaseDriver from "@/components/ShowcaseDriver";
import { LeadForm } from "@/pages/VaultEntrance";
import { Radar, Gauge, Smartphone, ArrowLeft, ArrowRight, Play, Mail, ChevronRight, Rocket } from "lucide-react";

const CONTACT = "admin@armorbatch.com";
const CHAPTERS = [
  {
    key: "war-room", title: "War Room", Icon: Radar, Comp: ShowcaseWarRoom,
    blurb: "Every job, every truck, one screen. Live ETAs, AI predicted returns, and a safety lockout that stops a truck leaving without its pre-start.",
    points: ["Three-column dispatch board", "Live GPS with predicted return times", "Pre-start safety lockout"],
  },
  {
    key: "cockpit", title: "Batcher Cockpit", Icon: Gauge, Comp: ShowcaseBatcher,
    blurb: "Weights straight off the scale head. Targets, tolerance and deviation per material, with the sequence calling the next move out loud.",
    points: ["Live scale telemetry", "±1% tolerance per material", "Docket issued the moment the load is done"],
  },
  {
    key: "driver", title: "Driver App", Icon: Smartphone, Comp: ShowcaseDriver,
    blurb: "The driver taps through the delivery on their phone. Works with no signal and syncs when it's back, so the docket is never lost.",
    points: ["Offline-first, syncs automatically", "Slump and water/cement checks on site", "Customer signs on the phone"],
  },
];

export default function PublicTour() {
  const [open, setOpen] = useState(null);
  const idx = CHAPTERS.findIndex((c) => c.key === open);
  const chapter = idx >= 0 ? CHAPTERS[idx] : null;

  if (chapter) {
    const next = CHAPTERS[(idx + 1) % CHAPTERS.length];
    return (
      <div className="fixed inset-0 bg-black flex flex-col" data-testid={`tour-playing-${chapter.key}`}>
        <div className="h-11 shrink-0 border-b border-zinc-800 bg-[#0E1013] flex items-center gap-2 px-2">
          <button type="button" onClick={() => setOpen(null)} data-testid="tour-back"
            className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white border border-zinc-700 px-3 py-1.5 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> All chapters
          </button>
          <span className="mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 ml-2 truncate">
            Chapter {idx + 1} · {chapter.title}
          </span>
          <span className="ml-auto mono text-[9px] uppercase tracking-[0.25em] text-zinc-600 hidden sm:inline">demo data</span>
          <button type="button" onClick={() => setOpen(next.key)} data-testid="tour-next"
            className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-black font-black bg-[#F57C00] px-3 py-1.5 ml-2">
            {next.title} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="relative flex-1 min-h-0">
          <chapter.Comp />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C0E] text-zinc-200" data-testid="public-tour">
      <div className="border-b border-zinc-800 px-5 py-4 flex items-center gap-3">
        <div className="w-6 h-6 bg-[#F57C00] flex items-center justify-center"><div className="w-2.5 h-2.5 bg-black" /></div>
        <div className="mono text-[10px] tracking-[0.4em] uppercase text-zinc-400">ArmorBatch Global</div>
        <a href={`mailto:${CONTACT}?subject=${encodeURIComponent("ArmorBatch Global — access request")}`} data-testid="tour-signin"
          className="ml-auto mono text-[9px] uppercase tracking-[0.3em] text-zinc-500 hover:text-white inline-flex items-center gap-1">
          Contact for access <ChevronRight className="w-3 h-3" />
        </a>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-12 lg:py-16">
        <div className="mono text-[10px] tracking-[0.4em] uppercase text-[#F57C00]">Take your time</div>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.04] text-white">
          The whole plant,<br />one chapter at a time.
        </h1>
        <p className="mt-5 text-base text-zinc-400 max-w-xl">
          Dispatch, batching and delivery — the same three screens your crew would live in.
          Pick one and watch it run for as long as you like.
        </p>

        <div className="mt-12 grid gap-4 md:grid-cols-3">
          {CHAPTERS.map((c, i) => (
            <button key={c.key} type="button" onClick={() => setOpen(c.key)} data-testid={`tour-chapter-${c.key}`}
              className="group text-left border border-zinc-800 bg-[#101215] p-5 hover:border-[#F57C00] hover:-translate-y-1 transition-all duration-300">
              <div className="flex items-center gap-2.5">
                <c.Icon className="w-5 h-5 text-[#F57C00]" />
                <span className="mono text-[9px] uppercase tracking-[0.3em] text-zinc-600">Chapter {i + 1}</span>
              </div>
              <div className="heading text-2xl font-black !text-white mt-3">{c.title}</div>
              <p className="text-sm text-zinc-400 mt-2 leading-snug">{c.blurb}</p>
              <ul className="mt-4 space-y-1.5">
                {c.points.map((p) => (
                  <li key={p} className="mono text-[10px] uppercase tracking-wide text-zinc-500 flex items-start gap-2">
                    <span className="text-[#F57C00] mt-px">▸</span>{p}
                  </li>
                ))}
              </ul>
              <div className="mt-5 inline-flex items-center gap-2 mono text-[10px] uppercase tracking-widest font-black text-black bg-[#F57C00] px-3 py-2 group-hover:gap-3 transition-all">
                <Play className="w-3.5 h-3.5" /> Watch it run
              </div>
            </button>
          ))}
        </div>

        {/* pitch */}
        <div className="mt-16 border-t border-zinc-800 pt-10 grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="text-lg md:text-lg font-black uppercase tracking-tight text-white">Secure your plant</h2>
            <p className="text-sm text-zinc-400 mt-4 max-w-md">
              Every screen you just watched, unlimited users, unlimited dockets. Start a 14-day trial or
              tell us about your plant and we'll quote you properly.
            </p>
            <Link to="/signup" data-testid="tour-signup-btn"
              className="mt-6 mr-3 inline-flex items-center gap-2.5 bg-[#F57C00] hover:bg-[#ff8f1f] text-black text-base font-black uppercase tracking-widest px-7 py-3.5 transition-colors duration-300">
              <Rocket className="w-4.5 h-4.5" /> Start 14-day trial
            </Link>
            <a href={`mailto:${CONTACT}?subject=${encodeURIComponent("ArmorBatch Global — enquiry")}`} data-testid="tour-contact-btn"
              className="mt-6 inline-flex items-center gap-2.5 bg-[#1D4ED8] hover:bg-[#1A43BA] text-white text-base font-bold px-7 py-3.5 transition-colors duration-300">
              <Mail className="w-4.5 h-4.5" /> Contact Us
            </a>
          </div>
          <div className="[&_.mono]:text-zinc-500 [&_input]:bg-transparent [&_input]:border-zinc-700 [&_input]:!text-zinc-100 [&_input]:placeholder:text-zinc-600">
            <LeadForm dark />
          </div>
        </div>
      </div>
    </div>
  );
}
